import { useState } from 'react';
import { useRouter } from 'next/router';

import { AdornedInput, FormField, FormHelp, Label } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyFloorPlanEditWebApiHandler_squareFeet } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useFormMode } from '../../../contexts/FormModeContext';
import { useScreenSize } from '../../../hooks';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import propertyUtils from '../../../utils/propertyUtils';

import { useWizardNavigation } from '../../CreateListingPage/WizardNavigationProvider';

const inputName = 'squareFeet';

const SquareFootageFormField = ({ isRelistingFieldset = false }: { isRelistingFieldset?: boolean }) => {
  const router = useRouter();
  const { isScreenDesktop } = useScreenSize();
  const formModeContext = useFormMode();
  const navigation = useWizardNavigation();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const floorPlans = listingDetails?.floorPlans || [];
  const floorPlanId = floorPlans[0]?.id;
  const initialSquareFeet = floorPlans[0]?.squareFeet ?? null;
  const roomSquareFeet = floorPlans[0]?.roomForRent?.roomSquareFeet;

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });
  const label = isRoomForRent ? 'Property square footage' : 'Square footage';

  const [customErrorMessage, setCustomErrorMessage] = useState<string | null>(null);

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_squareFeet,
  });

  const squareFeetField = useFieldSave({
    fieldName: inputName,
    initialValue: initialSquareFeet,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      const numValue = value == undefined ? undefined : Number(value);
      return {
        getParams: { floorPlanId: floorPlanId!, squareFeet: numValue },
      };
    },
  });

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : Number(e.target.value);
    if (value !== null && value >= 20000) {
      setCustomErrorMessage('Square footage values of 20,000 or more are unusually large. Verify this is correct.');
    } else if (Number(value) < roomSquareFeet) {
      setCustomErrorMessage(
        '‘Square footage’ field can only be set if the value is more than or equal to the room’s square footage',
      );
      if (formModeContext.mode === 'wizard') {
        navigation.reportError();
      }
    } else {
      navigation.clearError();
      setCustomErrorMessage(null);
    }

    squareFeetField.onChange(value);
  };

  return (
    <FormField
      className="SquareFootageFormField"
      label={<Label size={isRelistingFieldset ? 'lg' : 'md'}>{label}</Label>}
      error={!!squareFeetField.error || !!customErrorMessage}
      control={
        <AdornedInput
          size={isScreenDesktop ? 'lg' : 'md'}
          input={
            <AdornedInput.Input
              name={inputName}
              value={String(squareFeetField.value || '')}
              onChange={handleFieldChange}
              onBlur={squareFeetField.onBlur}
              aria-label={label}
            />
          }
          endAdornment={<AdornedInput.Adornment>sq. ft.</AdornedInput.Adornment>}
        />
      }
      description={<FormHelp>{squareFeetField.error || customErrorMessage}</FormHelp>}
    />
  );
};

export default SquareFootageFormField;
